<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import {
  analysisEquipmentList,
  analysisEquipmentCreate,
  analysisEquipmentDelete,
} from "@/common/api/rest/analysis/equipment";

import { equipmentPatch } from "@/common/api/rest/equipment";

import type { AnalysisType } from "@/common/types/analysis";
import type {
  EquipmentType,
  EquipmentListType,
  EquipmentPayloadType,
} from "@/common/types/equipment";

import {
  equipmentForm,
  equipmentDetailsForm,
  searchEquipment,
} from "@/components";
import {
  svgIcon,
  baseInput,
  baseModal,
  baseConfirm,
  baseTooltip,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisType | null;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const equipments = ref<EquipmentType[]>([]);
const selected = ref<any>(null);

const payload = ref<EquipmentPayloadType | null>(null);
const inputSearch = ref("");

const showModalAdd = ref(false);
const showDelConfirm = ref(false);

const equipmentsIds = computed(
  () => equipments.value?.map((item) => item.equipment_id) || [],
);

function listEquipments() {
  if (!props.data) return;

  analysisEquipmentList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        equipments.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function add() {
  showModalAdd.value = true;
}

function addSelect(data: any[]) {
  selected.value = data[0];
}

function addCancel() {
  selected.value = null;
  showModalAdd.value = false;
}

function addConfirm() {
  if (!props.data || !selected.value) return;

  analysisEquipmentCreate(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
    equipment_id: selected.value.equipment_id,
    equipment_name: selected.value.name,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        listEquipments();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });

  inputSearch.value = "";
  addCancel();
}

function del(data: EquipmentType) {
  selected.value = data;
  showDelConfirm.value = true;
}

function delCancel() {
  selected.value = null;
  showDelConfirm.value = false;
}

function delConfirm() {
  if (!props.data || !selected.value) return;

  analysisEquipmentDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        listEquipments();
        delCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  listEquipments();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-if="equipments.length === 0">
        <div class="form-empty">
          {{ t("analysis.texts.empty_equipment") }}
        </div>
      </template>

      <template v-else>
        <template v-for="(item, index) in equipments" :key="index">
          <div class="item">
            <div class="action">
              <span class="action-title">
                {{ t("common.labels.equipment") }} {{ index + 1 }}
              </span>

              <template v-if="isEdit">
                <base-tooltip position="relative">
                  <template #trigger>
                    <button
                      type="button"
                      class="btn btn-none btn-sm remove"
                      @click="del(item)"
                    >
                      <svg-icon icon="trash" size="1.75rem" />
                    </button>
                  </template>
                  <template #tooltip>
                    {{ t("common.labels.delete") }}
                  </template>
                </base-tooltip>
              </template>
            </div>

            <div class="content">
              <div class="avatar">
                <template v-if="item.avatar">
                  <img :src="item.avatar.small" />
                </template>
              </div>
              <div class="infos">
                <div class="line">
                  <label>{{ t("common.labels.code") }}: </label>
                  <span>{{ item?.code }}</span>
                </div>
                <div class="line">
                  <label>{{ t("common.labels.brand") }}: </label>
                  <span>{{ item?.brand }}</span>
                </div>
                <div class="line">
                  <label>{{ t("common.labels.name") }}: </label>
                  <span>{{ item?.name }}</span>
                </div>
                <div class="line">
                  <label>{{ t("common.labels.model") }}: </label>
                  <span>{{ item?.model }}</span>
                </div>
                <div class="line">
                  <label>{{ t("common.labels.serial_number") }}: </label>
                  <span>{{ item?.serial_number }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>

      <div class="actions">
        <button class="btn btn-md btn-primary" @click="add()">
          {{ t("common.labels.add").toUpperCase() }}
        </button>
      </div>
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.equipment'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-equipment
          :account-id="data?.account_id"
          :ids="equipmentsIds"
          :unic="true"
          @update-data="addSelect"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showDelConfirm"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.equipment'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: payload?.name,
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 1rem;
  padding-inline: 1.5rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.25rem;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .border {
    border-block-end: 1px solid var(--border);
    padding-block-end: 0.4rem;
  }

  .form-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }

  .actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-block: 1.5rem;
  }

  .item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    background-color: var(--background-off);
    border-bottom: 1px solid var(--border);
    margin-bottom: 1rem;
    padding-bottom: 2.5rem;

    .action {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding-block-end: 1rem;

      &-title {
        font-size: 1rem;
        font-weight: bold;
        text-transform: uppercase;
      }

      .remove {
        &:hover {
          color: var(--red);
        }
      }
    }

    .content {
      display: flex;
      flex-direction: row;
      gap: 2rem;

      .avatar {
        width: 150px;

        img {
          width: 100%;
        }
      }

      .infos {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .line {
          display: flex;
          flex-direction: row;
          gap: 0.5rem;
          background: #f1f1f1;
          padding: 0.5rem;

          label {
            font-weight: normal;
            flex: 1;
            font-size: 0.875rem;
          }

          span {
            font-weight: bold;
            flex: 4;
            font-size: 0.875rem;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-component {
    .item {
      .content {
        flex-direction: column;

        .avatar {
          display: flex;
          width: 100%;

          img {
            width: 250px;
            margin-inline: auto;
          }
        }

        .infos {
          .line {
            flex-direction: column;
          }
        }
      }
    }
  }
}
</style>
